<template>
  <div class="preika-insight-page">
    <section class="preika-page-hero preika-page-hero--cash">
      <div>
        <p class="preika-page-hero__eyebrow">BILLING & RECEIPT OUTLOOK</p>
        <h1>入出金予測</h1>
        <p>請求済み、発行準備、将来予測を支払期日ベースで並べ、月ごとの入金見込みを可視化します。</p>
      </div>
      <div class="preika-page-controls">
        <label><span>開始月</span><input v-model="fromMonth" type="month" @change="fetchData"></label>
        <label><span>表示期間</span>
          <select v-model.number="rangeMonths" @change="fetchData">
            <option :value="6">6か月</option>
            <option :value="12">12か月</option>
            <option :value="18">18か月</option>
          </select>
        </label>
        <button type="button" class="preika-btn preika-btn--primary" :disabled="loading" @click="fetchData"><CIcon name="cil-loop"/>再予測</button>
      </div>
    </section>

    <div v-if="loading" class="preika-analytics-loading"><span class="preika-analytics-spinner"></span>請求・入金見込みを集計しています</div>
    <div v-else-if="error" class="preika-analytics-error" role="alert">
      <div><strong>入出金予測を取得できませんでした</strong><small>{{ error }}</small></div>
      <button type="button" @click="fetchData">再取得</button>
    </div>

    <template v-else>
      <section class="preika-analytics-kpis preika-analytics-kpis--five">
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-yen"/></span>
          <small>期間入金見込み</small><strong>{{ money(data.totalExpectedReceipts) }}</strong><p>支払期日ベース</p>
        </article>
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-check-circle"/></span>
          <small>請求書発行済み</small><strong>{{ money(data.totalIssued) }}</strong><p>請求番号あり</p>
        </article>
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-description"/></span>
          <small>発行準備</small><strong>{{ money(data.totalReady) }}</strong><p>売上登録済み・番号未採番</p>
        </article>
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-chart-pie"/></span>
          <small>将来予測</small><strong>{{ money(data.totalForecast) }}</strong><p>継続している金額から算出</p>
        </article>
        <article class="preika-analytics-kpi" :class="{ 'preika-analytics-kpi--warning': Number(data.totalOverdue) > 0 }">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-warning"/></span>
          <small>支払期日経過</small><strong>{{ money(data.totalOverdue) }}</strong><p>入金消込は未判定</p>
        </article>
      </section>

      <section class="preika-analytics-card">
        <header class="preika-analytics-card__header">
          <div><p>MONTHLY RECEIPTS</p><h2>月別の入金見込み</h2></div>
          <div class="preika-finance-legend">
            <span><i class="issued"></i>発行済み</span>
            <span><i class="ready"></i>発行準備</span>
            <span><i class="forecast"></i>予測</span>
          </div>
        </header>

        <div v-if="months.length" class="preika-cash-chart" :style="{ '--preika-chart-columns': months.length }">
          <article v-for="month in months" :key="month.yearMonth" class="preika-cash-chart__column">
            <div class="preika-cash-chart__value">{{ compactMoney(month.expectedAmount) }}</div>
            <div class="preika-cash-stack" :title="monthTitle(month)">
              <span class="preika-cash-stack__issued" :style="{ height: stackHeight(month.issuedAmount) }"></span>
              <span class="preika-cash-stack__ready" :style="{ height: stackHeight(month.readyAmount) }"></span>
              <span class="preika-cash-stack__forecast" :style="{ height: stackHeight(month.forecastAmount) }"></span>
            </div>
            <strong>{{ compactMonth(month.yearMonth) }}</strong>
            <small>{{ month.invoiceCount || 0 }}件</small>
            <span v-if="Number(month.overdueAmount) > 0" class="preika-overdue-label">期日経過 {{ compactMoney(month.overdueAmount) }}</span>
          </article>
        </div>
        <div v-else class="preika-analytics-empty"><CIcon name="cil-money"/><strong>表示期間の入金見込みがありません</strong><p>売上管理・請求対象・金額設定を確認してください。</p></div>
      </section>

      <section class="preika-analytics-card">
        <header class="preika-analytics-card__header">
          <div><p>COMPANY OUTLOOK</p><h2>取引先別の入金見込み</h2></div>
          <router-link to="/admin/sales/salesManagement" class="preika-text-link">請求書管理を開く<CIcon name="cil-arrow-right"/></router-link>
        </header>

        <div v-if="companies.length" class="preika-table-scroll">
          <table class="preika-data-table preika-cash-table">
            <thead>
              <tr>
                <th>取引先</th>
                <th>支払サイト</th>
                <th>期間見込み</th>
                <th>期日経過</th>
                <th v-for="month in months" :key="month.yearMonth">{{ compactMonth(month.yearMonth) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="company in companies" :key="company.companyId">
                <th scope="row"><strong>{{ company.companyName }}</strong></th>
                <td>{{ company.usance || 30 }}日</td>
                <td><strong>{{ money(company.totalExpectedAmount) }}</strong></td>
                <td :class="{ 'preika-negative-value': Number(company.totalOverdueAmount) > 0 }">{{ money(company.totalOverdueAmount) }}</td>
                <td v-for="value in company.months" :key="value.yearMonth" class="preika-cash-table__month">
                  <strong>{{ compactMoney(value.expectedAmount) }}</strong>
                  <small v-if="Number(value.forecastAmount) > 0">予測 {{ compactMoney(value.forecastAmount) }}</small>
                  <small v-else-if="Number(value.readyAmount) > 0">準備 {{ compactMoney(value.readyAmount) }}</small>
                  <small v-else-if="Number(value.issuedAmount) > 0">発行済み</small>
                  <span v-if="Number(value.overdueAmount) > 0" class="preika-source-dot preika-source-dot--danger" title="支払期日経過"></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="preika-analytics-empty preika-analytics-empty--compact">表示対象の取引先はありません。</div>
      </section>

      <p class="preika-analytics-note preika-analytics-note--important"><CIcon name="cil-lightbulb"/>{{ data.note }}</p>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import {
  compactManagementYearMonth,
  formatManagementMoney
} from '../../utils/managementInsights'
import { safeErrorDigest } from '../../utils/safeResponseMessage'

export default {
  name: 'CashFlowForecast',

  data () {
    return {
      fromMonth: moment().format('YYYY-MM'),
      rangeMonths: 12,
      loading: false,
      error: '',
      data: {
        totalExpectedReceipts: 0,
        totalIssued: 0,
        totalReady: 0,
        totalForecast: 0,
        totalOverdue: 0,
        note: '',
        months: [],
        companies: []
      }
    }
  },

  computed: {
    months () { return Array.isArray(this.data.months) ? this.data.months : [] },
    companies () { return Array.isArray(this.data.companies) ? this.data.companies : [] },
    chartMaximum () { return Math.max(1, ...this.months.map(item => Number(item.expectedAmount || 0))) }
  },

  mounted () { this.fetchData() },

  methods: {
    money: formatManagementMoney,
    compactMonth: compactManagementYearMonth,

    fetchData () {
      const target = moment(this.fromMonth, 'YYYY-MM', true)
      if (!target.isValid()) return
      this.loading = true
      this.error = ''
      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/management/cash-flow-forecast', {
          params: { from: target.format('YYYYMM'), months: this.rangeMonths }
        })
        .then(response => {
          const value = response.data || {}
          this.data = {
            ...value,
            months: Array.isArray(value.months) ? value.months : [],
            companies: Array.isArray(value.companies) ? value.companies : []
          }
        })
        .catch(error => {
          this.error = error.response && error.response.status === 403
            ? 'この画面を表示するには管理者権限が必要です。社内の管理者にご相談ください。'
            : '通信または入金予測の処理でエラーが発生しました。時間をおいて再度お試しください。'
          console.error('入出金予測取得失敗:', safeErrorDigest(error))
        })
        .finally(() => { this.loading = false })
    },

    stackHeight (value) {
      return `${Math.max(0, Number(value || 0) / this.chartMaximum * 100)}%`
    },

    compactMoney (value) {
      const numeric = Number(value || 0)
      const sign = numeric < 0 ? '-' : ''
      const absolute = Math.abs(numeric)
      if (absolute >= 100000000) return `${sign}${(absolute / 100000000).toFixed(1)}億`
      if (absolute >= 10000) return `${sign}${Math.round(absolute / 10000).toLocaleString('ja-JP')}万`
      return `${sign}${Math.round(absolute).toLocaleString('ja-JP')}`
    },

    monthTitle (month) {
      return [
        `発行済み ${this.money(month.issuedAmount)}`,
        `発行準備 ${this.money(month.readyAmount)}`,
        `予測 ${this.money(month.forecastAmount)}`
      ].join(' / ')
    }
  }
}
</script>
